export type LandingPageImageAiGenerated = Record<string, true>

type LandingPageImageReference = {
  shopId: string
  fileName?: string
  optimizedBaseName?: string
}

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const getPathname = (value: string) => {
  try {
    return new URL(value, 'https://landing-page-image.local').pathname
  } catch {
    return value.split(/[?#]/)[0]
  }
}

const getQueryImageSources = (value: string) => {
  try {
    const url = new URL(value, 'https://landing-page-image.local')
    return ['url', 'src', 'image', 'imageUrl']
      .map(key => url.searchParams.get(key))
      .filter((entry): entry is string => Boolean(entry))
  } catch {
    return []
  }
}

const getImageReference = (value?: string): LandingPageImageReference | undefined => {
  const source = String(value || '').trim()
  if (!source) return undefined

  const pathname = safeDecode(getPathname(source))
  const directMatch = pathname.match(/^\/(?:api\/(?:images|media)|(?:images|media))\/landingpages\/([^/]+)\/([^/]+)$/i)
  if (directMatch) {
    return {
      shopId: directMatch[1],
      fileName: directMatch[2],
    }
  }

  const optimizedMatch = pathname.match(/^\/image\/landingpages\/([^/]+)\/(.+)-w\d+\.(?:avif|gif|jpe?g|png|webp)$/i)
  if (optimizedMatch) {
    return {
      shopId: optimizedMatch[1],
      optimizedBaseName: optimizedMatch[2],
    }
  }

  return undefined
}

const getAllImageReferences = (value?: string) => {
  const queue = [String(value || '').trim()]
  const visited = new Set<string>()
  const references: LandingPageImageReference[] = []

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current || visited.has(current)) continue
    visited.add(current)

    const reference = getImageReference(current)
    if (reference) references.push(reference)

    for (const nestedSource of getQueryImageSources(current)) {
      if (!visited.has(nestedSource)) queue.push(nestedSource)
    }
  }

  return references
}

const getBaseName = (fileName?: string) => String(fileName || '').replace(/\.[^.]+$/, '')

export const createLandingPageImageAiGeneratedMatcher = (
  images?: LandingPageImageAiGenerated,
) => {
  const markedImages = Object.entries(images || {})
    .filter(([, aiGenerated]) => aiGenerated === true)
    .flatMap(([key]) => getAllImageReferences(key))

  return (src?: string) => getAllImageReferences(src).some(source =>
    markedImages.some(marked => (
      source.shopId === marked.shopId
      && (
        (source.fileName && marked.fileName === source.fileName)
        || (source.optimizedBaseName && getBaseName(marked.fileName) === source.optimizedBaseName)
      )
    ))
  )
}

export const mergeLandingPageImageAiGenerated = (
  ...maps: Array<LandingPageImageAiGenerated | undefined>
): LandingPageImageAiGenerated | undefined => {
  const entries = maps.filter((map): map is LandingPageImageAiGenerated => Boolean(map && Object.keys(map).length > 0))
  if (entries.length === 0) return undefined

  return Object.assign({}, ...entries)
}
